import { useState } from 'react';
import { api, ApiError } from '../api.ts';
import { useSession } from '../session.tsx';
import { useRouter } from '../router.tsx';
import { Card, EmptyState, ErrorState, Field, Loading, Tag, useAsync, useToast } from '../ui.tsx';

interface Diagnostic {
  code: string; title: string; severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  evidence: string; probableCause: string; impact: string; recommendation: string;
  fix: { kind: string; label: string; params?: Record<string, unknown> } | null;
  videoId?: string; videoTitle?: string;
}

const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Crítico', high: 'Alto', medium: 'Médio', low: 'Baixo', info: 'Informativo',
};
const SEVERITY_TONE: Record<string, string> = {
  critical: 'danger', high: 'danger', medium: 'warn', low: 'info', info: 'info',
};

export function DiagnosticsPage(): JSX.Element {
  const { workspace, canWrite } = useSession();
  const { navigate } = useRouter();
  const toast = useToast();
  const [videoId, setVideoId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const videos = useAsync(() => api<{ videos: { id: string; title: string }[] }>('/api/videos?limit=200'), [workspace?.id]);
  const diagnostics = useAsync(
    () => api<{ diagnostics: Diagnostic[]; summary: { total: number; critical: number; high: number; medium: number } }>(
      `/api/diagnostics${videoId ? `?video=${videoId}` : ''}`,
    ),
    [workspace?.id, videoId],
  );

  async function applyFix(diagnostic: Diagnostic): Promise<void> {
    if (!diagnostic.fix) return;
    const key = diagnostic.code + (diagnostic.videoId ?? '');
    setBusy(key);
    try {
      const result = await api<{ applied: boolean; message?: string; navigate?: string; params?: any }>(
        '/api/diagnostics/fix',
        { body: { kind: diagnostic.fix.kind, params: diagnostic.fix.params ?? {} } },
      );
      if (result.applied) {
        toast(result.message ?? 'Correção aplicada.', 'ok');
        diagnostics.reload();
        return;
      }
      // Correções que exigem contexto abrem a tela certa.
      const target = diagnostic.fix.params?.videoId ?? diagnostic.videoId;
      switch (result.navigate) {
        case 'open_video':
        case 'open_element':
          if (target) navigate(`/app/videos/${target}`);
          break;
        case 'open_embed':
          if (target) navigate(`/app/videos/${target}`);
          else toast('Abra o vídeo e vá até a aba Embed.', 'info');
          break;
        case 'open_integrations':
          navigate('/app/integrations');
          break;
        default:
          toast('Nada a aplicar automaticamente.', 'info');
      }
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao aplicar a correção.', 'error');
    } finally {
      setBusy(null);
    }
  }

  const list = diagnostics.data?.diagnostics ?? [];
  const summary = diagnostics.data?.summary;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Diagnóstico</h1>
          <p className="page-sub">
            Problemas detectados automaticamente a partir dos eventos, da mídia e da configuração dos CTAs.
          </p>
        </div>
        <div style={{ width: 260 }}>
          <Field label="Vídeo" id="d-video">
            <select id="d-video" value={videoId} onChange={(e) => setVideoId(e.target.value)}>
              <option value="">Todos os vídeos</option>
              {(videos.data?.videos ?? []).map((video) => <option key={video.id} value={video.id}>{video.title}</option>)}
            </select>
          </Field>
        </div>
      </div>

      {summary && summary.total > 0 && (
        <div className="grid cols-4" style={{ marginBottom: 18 }}>
          <div className="stat"><p className="stat-label">Total</p><p className="stat-value">{summary.total}</p></div>
          <div className="stat"><p className="stat-label">Críticos</p><p className="stat-value" style={{ color: 'var(--danger)' }}>{summary.critical}</p></div>
          <div className="stat"><p className="stat-label">Altos</p><p className="stat-value" style={{ color: '#e5673f' }}>{summary.high}</p></div>
          <div className="stat"><p className="stat-label">Médios</p><p className="stat-value" style={{ color: 'var(--warn)' }}>{summary.medium}</p></div>
        </div>
      )}

      {diagnostics.loading && <Loading label="Analisando os dados…" />}
      {diagnostics.error && <ErrorState message={diagnostics.error} onRetry={diagnostics.reload} />}

      {!diagnostics.loading && list.length === 0 && (
        <EmptyState
          title="Nenhum problema detectado"
          description="Assim que houver volume suficiente de eventos, o diagnóstico aponta quedas de retenção, CTAs ignorados, falhas de atribuição e mídia incompatível."
        />
      )}

      <div className="grid" style={{ gap: 14 }}>
        {list.map((diagnostic) => {
          const key = diagnostic.code + (diagnostic.videoId ?? '');
          return (
            <article className={`diag ${diagnostic.severity}`} key={key + diagnostic.title}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h4>{diagnostic.title}</h4>
                  <div className="row tight">
                    <Tag tone={SEVERITY_TONE[diagnostic.severity]}>{SEVERITY_LABEL[diagnostic.severity]}</Tag>
                    {diagnostic.videoTitle && <span className="card-sub">{diagnostic.videoTitle}</span>}
                  </div>
                </div>
                {diagnostic.fix && (
                  <button
                    type="button"
                    className="btn small"
                    disabled={busy === key || (!canWrite && !diagnostic.fix.kind.startsWith('open_'))}
                    onClick={() => applyFix(diagnostic)}
                  >
                    {busy === key ? <span className="spinner-inline" /> : null} {diagnostic.fix.label}
                  </button>
                )}
              </div>

              <dl>
                <dt>Evidência</dt><dd>{diagnostic.evidence}</dd>
                <dt>Causa provável</dt><dd>{diagnostic.probableCause}</dd>
                <dt>Impacto</dt><dd>{diagnostic.impact}</dd>
                <dt>Recomendação</dt><dd>{diagnostic.recommendation}</dd>
              </dl>
            </article>
          );
        })}
      </div>

      <div style={{ height: 18 }} />
      <Card title="O que é verificado" subtitle="Regras aplicadas a cada análise.">
        <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-2)', columns: 2, columnGap: 32, fontSize: 13 }}>
          <li>Views sem play</li>
          <li>Abandono antes de 25%</li>
          <li>Abandono antes do primeiro CTA</li>
          <li>CTA exibido com pouco clique</li>
          <li>CTA oculto no mobile</li>
          <li>Cliques sem checkout</li>
          <li>Checkout sem compra</li>
          <li>UTMs ausentes</li>
          <li>Eventos duplicados</li>
          <li>Divergência entre views e eventos</li>
          <li>Conversão mobile baixa</li>
          <li>Mídia pesada</li>
          <li>MP4 sem faststart</li>
          <li>Página/player lento</li>
          <li>Vídeo incompatível</li>
          <li>Venda sem atribuição</li>
        </ul>
      </Card>
    </div>
  );
}
